import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

/**
 * Categories / entity-types — `/api/admin/entity-types` (ff-api admin.entity_type).
 *   GET  ?hierarchy=1 → tree, else flat → { entity_types:[...], ... }
 *   GET  /<etid> · POST · PUT /<etid> · DELETE /<etid>?hard=1
 * Deactivates by default; hard delete is refused with 409 `in_use` when the
 * type has child types or listings.
 */

const BASE = '/api/admin/entity-types';

export interface AdminEntityType {
  [key: string]: any;
  etid?: number;
  name_friendly?: string | null;
  name_system?: string | null;
  name_menu?: string | null;
  description?: string | null;
  etid_parent?: number | null;
  etid_root?: number | null;
  category?: string | null;
  icon?: string | null;
  active?: number | boolean | null;
  children?: AdminEntityType[];
}

export const entityTypeKeys = {
  all: ['admin-entity-types'] as const,
  list: (hierarchy: boolean) => [...entityTypeKeys.all, 'list', hierarchy] as const,
  detail: (etid: number) => [...entityTypeKeys.all, 'detail', etid] as const,
};

// ─── Queries ─────────────────────────────────────────────────────────────────

export const useEntityTypesQuery = (hierarchy = false, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: entityTypeKeys.list(hierarchy),
    enabled,
    queryFn: withErrorHandling(
      () =>
        request<{ entity_types: AdminEntityType[] }>(
          `${BASE}${hierarchy ? '?hierarchy=1' : ''}`
        ),
      'Failed to load categories'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useEntityTypeQuery = (etid: number | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: entityTypeKeys.detail(etid ?? -1),
    enabled: enabled && !!etid,
    queryFn: withErrorHandling(
      () => request<AdminEntityType>(`${BASE}/${etid}`),
      'Failed to load the category'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Mutations ───────────────────────────────────────────────────────────────

const useInvalidate = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: entityTypeKeys.all });
};

export const useCreateEntityTypeMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (fields: Record<string, unknown>) => request<any>(BASE, 'POST', fields),
      'Failed to create the category'
    ),
    onSuccess: invalidate,
  });
};

export const useUpdateEntityTypeMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { etid: number; fields: Record<string, unknown> }) =>
        request<any>(`${BASE}/${vars.etid}`, 'PUT', vars.fields),
      'Failed to update the category'
    ),
    onSuccess: invalidate,
  });
};

/** Deactivate by default; pass `hard` to hard-delete (409 `in_use` if it has children/listings). */
export const useDeleteEntityTypeMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { etid: number; hard?: boolean }) =>
        request<any>(`${BASE}/${vars.etid}${vars.hard ? '?hard=1' : ''}`, 'DELETE'),
      'Failed to delete the category'
    ),
    onSuccess: invalidate,
  });
};
